import { useAppContext } from '@common/contexts/AppContext';
import { getRoomExtras, roomsKeys } from '@features/rooms';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

const EXTRAS_STALE_TIME_MS = 60_000;

export type RoomExtrasOfferedParams = {
  roomId: string;
  rateId: string;
  adults: number;
  children: number;
  fromDate: string;
  toDate: string;
};

export const toJourneyExtrasParams = (
  journeyParams: ParsedJourneyParams,
  roomId: string,
  rateId: string
): RoomExtrasOfferedParams => ({
  roomId,
  rateId,
  adults: journeyParams.adults ?? 0,
  children: journeyParams.children ?? 0,
  fromDate: journeyParams.fromDate?.format('YYYY-MM-DD'),
  toDate: journeyParams.toDate?.format('YYYY-MM-DD'),
});

export const roomExtrasQuery = (siteId: number, params: RoomExtrasOfferedParams) => {
  const request = { ...params, childAges: {}, rooms: [] };
  return {
    queryKey: roomsKeys.roomExtras({ siteId, ...request }),
    queryFn: () => getRoomExtras({ siteId, ...request }),
    staleTime: EXTRAS_STALE_TIME_MS,
  };
};

export const hasExtras = (extras: unknown) => Array.isArray(extras) && extras.length > 0;

export const useRoomExtrasOffered = (params: RoomExtrasOfferedParams, enabled: boolean) => {
  const { siteId } = useAppContext();
  const { data, isSuccess, isError } = useQuery({
    ...roomExtrasQuery(siteId, params),
    enabled,
  });
  if (isError) return false;
  return isSuccess ? hasExtras(data) : undefined;
};

export const useCheckRoomExtrasOffered = () => {
  const { siteId } = useAppContext();
  const queryClient = useQueryClient();
  return useCallback(
    async (params: RoomExtrasOfferedParams) => {
      try {
        return hasExtras(await queryClient.fetchQuery(roomExtrasQuery(siteId, params)));
      } catch {
        return false;
      }
    },
    [queryClient, siteId]
  );
};
